import { ROUTE_PATHS, SHOPIFY_TEST_VENDOR_KEY } from "@client-shared/lib/constants";
import i18n from "@client-shared/lib/i18n";
import * as api from "@client-shared/services/api";
import { prepareWooCommerceAuthorizeUrl, sanitizeWooCommerceDomain } from "@client-shared/utilities";
import { Installation, Service } from "@shared/types";
import * as React from "react";
import { toast } from "react-toastify";
import { installShopifyService, installWooCommerceService } from "../services";
import { useServicesState } from "../services/useServicesState";
import useVendorContext from "../vendor/useCurrentVendor";
import {
  deleteInstallation as deleteInstallationAction,
  initialState,
  putInstallation as putInstallationAction,
  reducer,
  setError,
  setInitialized,
  setInstallations,
  setLoading,
} from "./actions";
import {
  DeleteInstallationKeys,
  InstallationsActions,
  InstallationsContextState,
  PutInstallationKeys,
  UseInstallationUtils as UseInstallationMethods,
} from "./types";

export const defaultContextState: InstallationsContextState = [initialState, () => undefined];

export const InstallationsContext = React.createContext(defaultContextState);
type Props = {
  children: React.ReactNode;
};

export const InstallationsProvider: React.FC<Props> = ({ children }: Props) => {
  const [state, dispatch] = React.useReducer(reducer, initialState);
  const [{ initialized: isVendorInitialized }] = useVendorContext();
  const [{ services }] = useServicesState();
  
  React.useEffect(() => {
    if (isVendorInitialized && !state.initialized) {
      dispatch(setInitialized(true));
    }
  }, [isVendorInitialized, state.initialized]);

  const useInstallationMethods: UseInstallationMethods = React.useMemo(() => {
    return {
      fetch: () => fetchInstallations(services, dispatch),
      put: (installation: Installation, message: PutInstallationKeys) => 
        putInstallation(installation, message, dispatch),
      delete: (installation: Installation, message: DeleteInstallationKeys) => 
        deleteInstallation(installation, message, dispatch),
      getSingle: (installationId: string) => 
        getSingleInstallation(installationId, dispatch),
      setError: (error: Error) => dispatch(setError(error)),
      installShopify: (params) => 
        installShopify(params, services, dispatch),
      installWooCommerce: (params) => 
        installWooCommerce(params),
    };
  }, [services]);

  const contextValue: InstallationsContextState = React.useMemo(
    () => [state, dispatch, useInstallationMethods],
    [state, dispatch, useInstallationMethods]
  );

  return <InstallationsContext.Provider value={contextValue}>{children}</InstallationsContext.Provider>;
};

// Side effects - defined outside the component 
const fetchInstallations = async (
  services: Map<string, Service>,
  dispatch: React.Dispatch<InstallationsActions>
) => {
  if (services.size === 0) return;
  dispatch(setLoading(true));
  try {
    const installations = await api.getInstallations();
    const filtered = installations.filter(
      (installation) =>
        installation.state !== "DELETED" &&
        installation.state !== "PENDING" &&
        services.get(installation.serviceId)?.serviceType === "remote"
    );
    dispatch(setInstallations(filtered));
    dispatch(setError(undefined));
    dispatch(setLoading(false));
    return filtered;
  } catch (e) {
    dispatch(
      setError({
        name: i18n.t("components.Installation.errors.getInstallations.name"),
        message: `${i18n.t("components.Installation.errors.getInstallations.message")} - ${e}`,
      })
    );
    dispatch(setLoading(false));
  }
};

const getSingleInstallation = async (
  installationId: string,
  dispatch: React.Dispatch<InstallationsActions>
) => {
  dispatch(setLoading(true));
  try {
    const installation = await api.getInstallation(installationId);
    if (installation.state === "DELETED" || installation.state === "PENDING") {
      dispatch(setLoading(false));
      dispatch(setError(new Error("Installation is not active")));
      return;
    }
    dispatch(setInstallations([installation]));
    dispatch(setError(undefined));
    dispatch(setLoading(false));
  } catch (e) {
    dispatch(setLoading(false));
    dispatch(setError(e as Error));
  }
};

const putInstallation = async (
  installation: Installation,
  message: PutInstallationKeys,
  dispatch: React.Dispatch<InstallationsActions>
) => {
  dispatch(setLoading(true));
  try {
    const result = await api.putInstallation(installation);
    dispatch(setError(undefined));
    dispatch(putInstallationAction(result));
    toast.success(i18n.t(`components.Installation.put.${String(message)}`));
    dispatch(setLoading(false));
  } catch (e) {
    dispatch(setLoading(false));
    dispatch(setError(e as Error));
  }
};

const deleteInstallation = async (
  installation: Installation,
  message: DeleteInstallationKeys,
  dispatch: React.Dispatch<InstallationsActions>
) => {
  dispatch(setLoading(true));
  try {
    const result = await api.deleteInstallation(installation);
    dispatch(deleteInstallationAction(result));
    toast.dark(i18n.t(`components.Installation.delete.${String(message)}`));
    dispatch(setError(undefined));
    dispatch(setLoading(false));
    return true;
  } catch (e) {
    dispatch(setLoading(false));
    dispatch(setError(e as Error));
    return false;
  }
};

const installShopify = async (
  params: {
    shop: string;
    accessToken: string;
    selectedLocale?: string | null;
    vendorKey: string;
    installUrl?: string;
  },
  services: Map<string, Service>,
  dispatch: React.Dispatch<InstallationsActions>
) => {
  const { shop, accessToken, selectedLocale, vendorKey, installUrl } = params;

  // Handle test vendor redirect
  if (vendorKey === SHOPIFY_TEST_VENDOR_KEY) {
    if (installUrl) {
      return { success: true, redirectUrl: installUrl };
    }
    return { success: false, error: new Error("Install URL not provided") };
  }

  if (!shop || !accessToken) {
    return { success: false, error: new Error("Shop and access token are required") };
  }

  try {
    await installShopifyService(shop, accessToken, selectedLocale ?? undefined);
    await fetchInstallations(services, dispatch); // Refresh installations list
    const redirectUrl = `${ROUTE_PATHS.SETTINGS}?success=1&shop=${shop}`;
    return { success: true, redirectUrl };
  } catch (e) {
    return { success: false, error: e as Error };
  }
};

const installWooCommerce = async (params: { 
  shop: string; 
  selectedLocale?: string | null; 
  serviceName: string;
}) => {
  const { shop, selectedLocale, serviceName } = params;

  if (!shop) {
    return { success: false, error: new Error("Shop domain is required") };
  }

  const clientHost = process.env.REACT_APP_CLIENT_HOST
    ? `https://${process.env.REACT_APP_CLIENT_HOST.replace(/https:\/\//, "")}`
    : undefined;
  const apiHost = process.env.REACT_APP_API_HOST
    ? `https://${process.env.REACT_APP_API_HOST.replace(/https:\/\//, "")}`
    : undefined;

  if (!apiHost || !clientHost) {
    return { success: false, error: new Error("API or Client host not configured") };
  }

  try {
    const domain = sanitizeWooCommerceDomain(shop);
    const jwtToken = await installWooCommerceService(domain, selectedLocale ?? undefined);

    if (!jwtToken) {
      return { success: false, error: new Error("Failed to get authorization token") };
    }

    const authorizeUrl = prepareWooCommerceAuthorizeUrl(domain, jwtToken, apiHost, clientHost);

    if (!authorizeUrl) {
      return {
        success: false,
        error: new Error(i18n.t("components.ServiceForm.error.authorizeURL", { service: serviceName })),
      };
    }

    return { success: true, redirectUrl: authorizeUrl };
  } catch (e) {
    return { success: false, error: e as Error };
  }
};
